import { addMinutes, isAfter } from 'date-fns';
import { ReoccurrenceEnum } from '@juno/client-api/model';

/**
 * Waits for time.
 * @param ms milliseconds to wait.  Defaults to 250 same as slowMo
 */
export async function timeout(ms = 250): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function secondsToHms(seconds: number) {
  seconds = Number(seconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor((seconds % 3600) % 60);

  return {
    hours: h,
    minutes: m,
    seconds: s,
  };
}

export function formatHmsShort(seconds: number) {
  const d = secondsToHms(seconds);
  const h = String(d.hours).padStart(2, '0');
  const m = String(d.minutes).padStart(2, '0');
  const s = String(d.seconds).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

export function formatHmsLong(seconds: number) {
  const d = secondsToHms(seconds);
  const h = String(d.hours);
  const m = String(d.minutes);
  const s = String(d.seconds);
  return `${h} hours ${m} minutes ${s} seconds`;
}

export function formatHmsLongAbbreviated(seconds: number) {
  const d = secondsToHms(seconds);
  const h = String(d.hours);
  const m = String(d.minutes);
  const s = String(d.seconds);
  return `${h}hr ${m}min ${s}sec`;
}

export function secondsToHmsNumber(seconds: number) {
  // Convert seconds to hhmmss (like you would enter into a microwave)
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const hhmmss = [h, m, s]
    .map((v) => (v < 10 ? `0${v}` : v))
    .filter((v, i) => v !== '00' || i > 0)
    .join('');
  return Number(hhmmss) || 0;
}

// --- Date and Time Utilities for creating sessions ---
export const generateTimeSlots = (stepMinutes = 30): string[] => {
  const times: string[] = [];
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  for (let i = 0; i < 24 * 60; i += stepMinutes) {
    times.push(
      date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
    );
    date.setMinutes(date.getMinutes() + stepMinutes);
  }
  return times;
};

export const getNextTimeSlot = (stepMinutes = 30): string => {
  const now = new Date();
  const minutes = now.getMinutes();
  const nextMinutes = Math.ceil(minutes / stepMinutes) * stepMinutes;
  now.setMinutes(nextMinutes, 0, 0);
  return now.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const parseTimeStringToDate = (time: string): Date => {
  const [timePart, modifier] = time.split(' ');
  const [hoursStr, minutesStr] = timePart.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (modifier === 'PM' && hours !== 12) hours += 12;
  if (modifier === 'AM' && hours === 12) hours = 0;

  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
};

export const formatTime = (date: Date): string =>
  date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

export const hhmmToMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

export const addDurationToDate = (date: Date, duration: string | number): Date => {
  const mins = typeof duration === 'number' ? duration : hhmmToMinutes(duration);
  return new Date(date.getTime() + mins * 60 * 1000);
};

export const safeToDate = (val: string | number | Date | null | undefined): Date | null => {
  if (!val) return null;
  const parsed = new Date(val);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const calculateAndSetEndDate = (
  startDateRaw: string | undefined,
  durationMinutes: number,
  setFieldValue: (field: string, value: any) => void,
): void => {
  const startDate = new Date(startDateRaw || new Date());
  const endDate = addMinutes(startDate, durationMinutes);
  setFieldValue('date_end', endDate.toISOString());
  setFieldValue('time_end', endDate.toISOString());
};

export const handleStartDateChange = (
  newValue: any,
  setFieldValue: (field: string, value: any) => void,
  currentEndRaw: string | null | undefined,
  duration: number,
): void => {
  const newStartDate = safeToDate(newValue);
  const currentEndDate = safeToDate(currentEndRaw);
  if (!newStartDate) return;

  setFieldValue('date_start', newStartDate.toISOString());

  if (!currentEndDate || !isAfter(currentEndDate, newStartDate)) {
    const newEndDate = addMinutes(newStartDate, duration);
    setFieldValue('date_end', newEndDate.toISOString());
    setFieldValue('time_end', newEndDate.toISOString());
  }
};

export const normalizeDateStartAndEnd = ({
  values,
  setFieldValue,
  duration,
  timeOptions,
}: {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  duration: string | number;
  timeOptions: string[];
}): void => {
  const defaultStartTime = getNextTimeSlot(30);
  const current = values.date_start ? new Date(values.date_start) : null;
  let displayTime = defaultStartTime;

  if (current) {
    const formatted = formatTime(current);
    if (timeOptions.includes(formatted)) {
      displayTime = formatted;
    }
  }

  const finalStart = parseTimeStringToDate(displayTime);
  const baseDate = values.date_start ? new Date(values.date_start) : new Date();
  const finalStartWithDate = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate(),
    finalStart.getHours(),
    finalStart.getMinutes(),
    0,
    0,
  );

  setFieldValue('date_start', finalStartWithDate.toISOString());

  if (values.reoccurrence === ReoccurrenceEnum.once) {
    const newEnd = addDurationToDate(finalStartWithDate, duration);
    setFieldValue('date_end', newEnd.toISOString());
    setFieldValue('time_end', newEnd.toISOString());
  }
};
